import type {ReactElement} from 'react';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {Checkbox} from '@Components/checkbox';
import {Text, TextSize} from '@Components/text';
import {LabelledCheckboxSelectionTheme} from '@Components/labelled-checkbox/labelled-checkbox.types';
import {noop} from '@Utils/general.util';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './labelled-checkbox.module.scss';

export interface LabelledCheckboxProps {
  label: string;
  isChecked: boolean;
  className?: string;
  onChange?: (isChecked: boolean) => void;
  textSize?: TextSize;
  textClassName?: string;
  checkboxClassName?: string;
  checkboxType?: CHECKBOX_TYPE;
  checkboxSize?: IconSize;
  selectionTheme?: LabelledCheckboxSelectionTheme;
  hasHover?: boolean;
  labelIcon?: string;
  isDisabled?: boolean;
  /**
   * swaps the checkbox for a spinner occupying the exact same footprint, for when the checked state is
   * pending on a request. the row is non-interactive while it is set.
   */
  isLoading?: boolean;
  labelInfo?: ReactElement<any>;
  labelIconClassName?: string;
}

export function LabelledCheckbox({
  label,
  checkboxType,
  isChecked = false,
  className = '',
  onChange = noop,
  textSize = TextSize.XSMALL,
  textClassName = '',
  checkboxClassName = '',
  checkboxSize = IconSize.SIZE_ICON_12,
  selectionTheme = LabelledCheckboxSelectionTheme.NONE,
  hasHover = false,
  labelIcon = undefined,
  isDisabled = false,
  isLoading = false,
  labelInfo = undefined,
  labelIconClassName = '',
}: LabelledCheckboxProps): ReactElement<any> {
  const getClassesForSelectionTheme = (): string => {
    switch (selectionTheme) {
      case LabelledCheckboxSelectionTheme.PRIMARY:
        return 'bg-primary-10';

      default:
        return '';
    }
  };

  const getTextColorForSelectionTheme = (): string => {
    switch (selectionTheme) {
      case LabelledCheckboxSelectionTheme.PRIMARY:
        return 'content-primary';

      default:
        return '';
    }
  };

  const shouldTextBeBold = (): boolean => {
    return isChecked && selectionTheme !== LabelledCheckboxSelectionTheme.NONE;
  };

  const getContainerClasses = (): string => {
    const classes = [styles.checkboxRow, 'flex-row-align-center', 'hover-transition-all'];

    if (isChecked) {
      classes.push(`${getClassesForSelectionTheme()} ${styles.checked}`);
    }

    if (isDisabled) {
      classes.push(styles.isDisabled);
    }

    if (isLoading) {
      classes.push(styles.isLoading);
    }

    if (hasHover) {
      classes.push(styles.hoverBg);
    }

    return classes.join(' ');
  };

  const getCheckboxType = (): CHECKBOX_TYPE | undefined => {
    if (isDisabled) {
      return CHECKBOX_TYPE.LIGHT;
    }

    return checkboxType;
  };

  const getCheckboxClassName = (): string => {
    const classes = [checkboxClassName];

    if (isDisabled) {
      classes.push(styles.disabledCheckbox);
    }

    if (isLoading) {
      classes.push(styles.hiddenCheckbox);
    }

    return classes.join(' ');
  };

  /**
   * the checkbox stays mounted and merely invisible while loading so the spinner inherits its exact
   * footprint — sizing the wrapper by hand would drift from whatever `checkboxSize` renders.
   */
  const getCheckbox = (): ReactElement => {
    const checkbox = <Checkbox className={getCheckboxClassName()} isChecked={isChecked} type={getCheckboxType()} size={checkboxSize} disabled={isDisabled} />;

    if (!isLoading) {
      return checkbox;
    }

    return (
      <span className={styles.checkboxLoaderWrapper}>
        {checkbox}
        <CircularProgressLoader classes="-no-margin" size={LOADER_SIZE.XXSMALL} color={LOADER_COLOR.PRIMARY} />
      </span>
    );
  };

  const onChangeHander = (): void => {
    if (isDisabled || isLoading) {
      return;
    }

    onChange(!isChecked);
  };

  return (
    <div className={`${getContainerClasses()} ${className}`} onClick={onChangeHander}>
      {getCheckbox()}
      {labelIcon ? <Icon className={`spacing-m-l-3 ${labelIconClassName}`} icon={labelIcon} size={IconSize.SIZE_ICON_16} /> : undefined}
      <Text
        val={label}
        size={textSize}
        className={`${labelIcon ? 'spacing-m-l-1' : 'spacing-m-l-2'}  ${isChecked ? getTextColorForSelectionTheme() : ''} ${textClassName}`}
        bold={shouldTextBeBold()}
      />
      {labelInfo ?? undefined}
    </div>
  );
}
